import type { DocumentLineageResponse, DocumentVersionResponse } from "@/api";

/** What's shown for one document: which lineage, and which of its versions. */
export interface OpenDocument {
  lineageUuid: string;
  /** The version being previewed; undefined means the latest. */
  attachmentUuid?: string;
}

export function versionsNewestFirst(doc: DocumentLineageResponse): DocumentVersionResponse[] {
  return [...(doc.versions ?? [])].sort((a, b) => b.version - a.version);
}

/** `canonicalFilename` is the backend's lower-cased matching key; show the newest version's real name. */
export function displayName(doc: DocumentLineageResponse): string {
  return versionsNewestFirst(doc)[0]?.filename ?? doc.canonicalFilename;
}

const KNOWN_KINDS = new Set(["docx", "xlsx", "pptx", "pdf", "md", "csv", "html"]);

/** Raster images a document can be, with the type they're shown as (never SVG, which can carry script). */
const IMAGE_TYPES: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
};

/** The image type to show a document as, or undefined when it isn't an image. */
export function imageTypeOf(ext: string): string | undefined {
  return Object.hasOwn(IMAGE_TYPES, ext) ? IMAGE_TYPES[ext] : undefined;
}

/** File extension of a document ("docx"), lower-cased; `documentType` when the name has none. */
export function extensionOf(filename: string, documentType?: string): string {
  const ext = /\.([a-z0-9]+)$/i.exec(filename)?.[1];
  return (ext ?? documentType ?? "").toLowerCase();
}

/** i18n key + values for a friendly file kind ("Word document", "JPG image"), falling back to "<EXT> file". */
export function kindKey(ext: string): { key: string; ext: string } {
  let key = "kinds.other";
  if (KNOWN_KINDS.has(ext)) key = `kinds.${ext}`;
  else if (imageTypeOf(ext)) key = "kinds.image";
  return { key, ext: ext.toUpperCase() };
}

/**
 * One card per document, under the reply that made its latest version. Older versions live in the
 * panel's Versions tab, so earlier replies don't repeat a stale card.
 */
export function documentsByMessage(
  documents: readonly DocumentLineageResponse[],
): Map<string, { doc: DocumentLineageResponse; version: DocumentVersionResponse }[]> {
  const byMessage = new Map<string, { doc: DocumentLineageResponse; version: DocumentVersionResponse }[]>();
  for (const doc of documents) {
    const version = versionsNewestFirst(doc)[0];
    if (!version?.messageUuid) continue;
    const list = byMessage.get(version.messageUuid) ?? [];
    list.push({ doc, version });
    byMessage.set(version.messageUuid, list);
  }
  return byMessage;
}
